/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { LuisSeparator } from 'src/core/components/LuisSeparator';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { FaultyIntentsList } from 'src/luis/projects/analytics/components/IntentsWithErrorsCard/SideBar/FaultyIntentsList';
import { Recommendations } from 'src/luis/projects/analytics/components/IntentsWithErrorsCard/SideBar/Recommendations';
import { IntentMetadata } from 'src/luis/projects/analytics/types/IntentMetadata';
import { LuisId } from 'src/luis/types/Resource';

export type SideBarProps = {
    /**
     * The selected faulty intent.
     */
    selectedIntent: IntentMetadata;

    /**
     * A callback which is invoked when selected intent changes.
     */
    onSelectedIntentChange?: (intent: IntentMetadata) => void;

    /**
     * A callback to load some intent trining result if needed from the backend.
     */
    loadIntentTrainingResult: (intentId: LuisId) => void;
};

const dashboardTheme = getScopedTheme('Dashboard');

/**
 * Displays faulty intent list with option to be changed with different filters
 * and recommendation for the selected one.
 *
 * @param props The properties to reflect the selection in the side bar.
 */
export const SideBar = (props: SideBarProps) => {
    const { selectedIntent, onSelectedIntentChange, loadIntentTrainingResult } = props;
    const { rootStore } = useGlobalState();

    const { analyticsStore } = rootStore.currentLuisAppStore.currentAppVersionStore;

    const intentsList = [
        ...analyticsStore.needyIntents.slice(0, 3),
        ...analyticsStore.greedyIntents.slice(0, 3),
        ...analyticsStore.unclearIntents.slice(0, 3),
        ...analyticsStore.incorrectIntents.slice(0, 3)
    ]
        .filter((item, i, arr) => arr.map(a => a.intentId).indexOf(item.intentId) === i)
        .sort((a, b) => (a.intentName < b.intentName ? -1 : 1));

    return (
        <Stack tokens={{ maxWidth: dashboardTheme.sidebarWidth }} horizontal horizontalAlign="space-between">
            <Stack>
                <FaultyIntentsList
                    intents={Array.from(analyticsStore.utterancesPerIntent.values())}
                    mostProblematicIntents={intentsList}
                    onSelectedIntentChange={onSelectedIntentChange}
                />
                <Recommendations selectedIntent={selectedIntent} loadIntentTrainingResult={loadIntentTrainingResult} />
            </Stack>
            <LuisSeparator vertical />
        </Stack>
    );
};
